import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {ArrowDownTrayIcon} from '@heroicons/react/24/outline';
import {regenerateBackupCodes, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, Input, Label} from '../Components';
import {Dialog} from './Dialog';

interface RegenerateBackupCodesDialogProps {
  onClose: () => void;
  onDone: () => void;
}

/** New codes replace the old set outright, and like enrollment they are shown exactly once. */
export const RegenerateBackupCodesDialog: FC<RegenerateBackupCodesDialogProps> = observer(({onClose, onDone}) => {
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const handleGenerate = () => {
    setWorking(true);
    setError('');
    regenerateBackupCodes(VerdocsEndpoint.getDefault(), code.trim())
      .then((r) => {
        setWorking(false);
        setCodes(r.backup_codes);
      })
      .catch((e) => {
        setWorking(false);
        setCode('');
        setError(e.response?.data?.error || 'Invalid code. Please try again.');
      });
  };

  const handleDownload = () => {
    const blob = new Blob([`Verdocs backup codes\n\n${codes.join('\n')}\n`], {type: 'text/plain'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'verdocs-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog onCancel={codes.length ? () => undefined : onClose}>
      <div className="overflow-y-auto">
        <div className="mb-1 text-lg font-medium text-[#33364b]">New backup codes</div>

        {codes.length === 0 ? (
          <>
            <div className="mb-4 text-[13px] text-gray-500">
              NOTE: Any previous codes will be invalid and may be deleted. Enter a code from your authenticator to continue.
            </div>
            <div className="mb-4 flex flex-col gap-2">
              <Label>Code from your app</Label>
              <Input
                value={code}
                autoFocus
                inputMode="numeric"
                placeholder="123456"
                className="w-40 font-mono"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              {error && <div className="text-[12px] text-[#bb2e3e]">{error}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" label="Cancel" onClick={onClose} />
              <Button label="Generate" disabled={working || code.length !== 6} onClick={handleGenerate} />
            </div>
          </>
        ) : (
          <>
            <div className="mb-3 text-[13px] text-gray-500">
              Each backup code may be used only once. Store them somewhere safe. Any previous codes you may have saved are no longer valid
              and may be deleted.
            </div>
            <div className="grid grid-cols-2 gap-x-10 gap-y-1.5 whitespace-nowrap rounded-md border border-[#ebecf0] bg-[#fbfbfc] p-4 font-mono text-[13px] text-[#33364b]">
              {codes.map((backupCode) => (
                <div key={backupCode}>{backupCode}</div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="small"
                variant="secondary"
                label="Download"
                icon={<ArrowDownTrayIcon className="h-4 w-4" />}
                onClick={handleDownload}
              />
              <Button
                size="small"
                variant="secondary"
                label="Copy"
                onClick={() => {
                  navigator.clipboard.writeText(codes.join('\n')).catch(() => undefined);
                  toast.success('Backup codes copied');
                }}
              />
            </div>
            <div className="mt-5 flex justify-end">
              <Button label="Done" onClick={onDone} />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
});
